'use client'

import * as React from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { auth, db } from '../../firebase'
import { getAccessLevel, canEdit, canViewAll } from 'lib/access'

const UserContext = React.createContext({
  user: null,
  accessLevel: 'limited',
  canEdit: false,
  canViewAll: false,
  loading: true,
})

const userCollectionName = 'users'

async function fetchUserProfile(userEmail, userUid) {
  const usersRef = collection(db, userCollectionName)
  if (userEmail) {
    const usersQuery = query(usersRef, where('email', '==', userEmail))
    const snapshot = await getDocs(usersQuery)
    if (!snapshot.empty) {
      const docSnapshot = snapshot.docs[0]
      return { id: docSnapshot.id, ...docSnapshot.data() }
    }
  }
  if (userUid) {
    const usersQuery = query(usersRef, where('userId', '==', userUid))
    const snapshot = await getDocs(usersQuery)
    if (!snapshot.empty) {
      const docSnapshot = snapshot.docs[0]
      return { id: docSnapshot.id, ...docSnapshot.data() }
    }
  }
  return null
}

export function UserProvider({ children }) {
  const [state, setState] = React.useState({
    user: null,
    accessLevel: 'limited',
    canEdit: false,
    canViewAll: false,
    loading: true,
  })

  React.useEffect(() => {
    let isMounted = true
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        if (!isMounted) return
        setState({
          user: null,
          accessLevel: 'limited',
          canEdit: false,
          canViewAll: false,
          loading: false,
        })
        return
      }

      try {
        const profile = await fetchUserProfile(authUser.email, authUser.uid)
        if (!isMounted) return
        const userType = profile?.userType || ''
        const level = getAccessLevel(userType)
        setState({
          user: {
            uid: authUser.uid,
            email: authUser.email || profile?.email || '',
            fullName: profile?.fullName || authUser.displayName || authUser.email || 'User',
            userType,
            userId: profile?.userId || authUser.uid,
            profileId: profile?.id || '',
          },
          accessLevel: level,
          canEdit: canEdit(userType),
          canViewAll: canViewAll(userType),
          loading: false,
        })
      } catch {
        if (!isMounted) return
        setState({
          user: {
            uid: authUser.uid,
            email: authUser.email || '',
            fullName: authUser.displayName || authUser.email || 'User',
            userType: '',
            userId: authUser.uid,
            profileId: '',
          },
          accessLevel: 'limited',
          canEdit: false,
          canViewAll: false,
          loading: false,
        })
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [])

  return <UserContext.Provider value={state}>{children}</UserContext.Provider>
}

export function useUser() {
  return React.useContext(UserContext)
}
